import { createFileRoute, notFound, Link } from "@tanstack/react-router";
import { Phone, ArrowRight, ChevronRight, Check } from "lucide-react";
import { openQuoteModal } from "../components/quote-modal";
import { Breadcrumbs } from "../components/breadcrumbs";
import { getGuide as getGuideSync } from "../data/guides";
import { GuideCityLinks } from "../components/guide-city-links";
import { InlineQuoteSection } from "@/components/inline-quote-section";

const SITE = "https://capitalpropressurewashing.com";

export const Route = createFileRoute("/guides/$slug")({
  loader: async ({ params }) => {
    const { getGuide } = await import("../data/guides");
    const guide = getGuide(params.slug);
    if (!guide) throw notFound();
    return {
      slug: guide.slug,
      title: guide.title,
      metaTitle: guide.metaTitle,
      description: guide.description,
      heroImage: guide.heroImage,
      published: guide.published,
      faqs: guide.faqs,
    };
  },
  head: ({ loaderData, params }) => {
    if (!loaderData) {
      return {
        meta: [
          { title: "Guide Not Found | Capital Pro Pressure Washing" },
          { name: "robots", content: "noindex" },
        ],
      };
    }
    const url = `${SITE}/guides/${params.slug}`;
    const image = loaderData.heroImage.startsWith("http")
      ? loaderData.heroImage
      : `${SITE}${loaderData.heroImage}`;
    return {
      meta: [
        { title: loaderData.metaTitle },
        { name: "description", content: loaderData.description },
        { property: "og:title", content: loaderData.title },
        { property: "og:description", content: loaderData.description },
        { property: "og:url", content: url },
        { property: "og:type", content: "article" },
        { property: "og:image", content: image },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:title", content: loaderData.title },
        { name: "twitter:description", content: loaderData.description },
        { name: "twitter:image", content: image },
      ],
      links: [{ rel: "canonical", href: url }],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            headline: loaderData.title,
            description: loaderData.description,
            image,
            datePublished: loaderData.published,
            dateModified: loaderData.published,
            author: { "@type": "Organization", name: "Capital Pro Pressure Washing" },
            publisher: { "@type": "Organization", name: "Capital Pro Pressure Washing", "@id": `${SITE}/#business` },
            mainEntityOfPage: url,
          }),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: loaderData.faqs.map((f) => ({
              "@type": "Question",
              name: f.q,
              acceptedAnswer: { "@type": "Answer", text: f.a },
            })),
          }),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "Home", item: `${SITE}/` },
              { "@type": "ListItem", position: 2, name: "Guides", item: `${SITE}/guides` },
              { "@type": "ListItem", position: 3, name: loaderData.title, item: url },
            ],
          }),
        },
      ],
    };
  },
  notFoundComponent: GuideNotFound,
  component: GuideRoute,
});

function GuideRoute() {
  const { slug } = Route.useLoaderData();
  const guide = getGuideSync(slug)!;

  return (
    <main>
      <Breadcrumbs items={[{ label: "Guides", href: "/guides" }, { label: guide.title }]} />

      {/* Hero */}
      <section className="relative bg-[var(--brand-deep-blue)] text-white overflow-hidden">
        <div className="relative mx-auto max-w-[900px] px-4 py-14 md:py-20 text-center">
          <h1
            className="text-2xl md:text-4xl uppercase leading-tight"
            style={{ fontFamily: "var(--font-display)", fontWeight: 700 }}
          >
            {guide.title}
          </h1>
          <p className="mt-4 text-white/85 text-base md:text-lg leading-relaxed">
            {guide.description}
          </p>
        </div>
      </section>

      <section className="bg-white">
        <div className="mx-auto max-w-[900px] px-4 -mt-8 md:-mt-10 relative z-10">
          <div className="aspect-[16/9] rounded-lg overflow-hidden shadow-xl">
            <img src={guide.heroImage} alt={guide.title} className="h-full w-full object-cover" loading="eager" />
          </div>
        </div>
      </section>

      {/* Intro */}
      <section className="bg-white py-10 md:py-12">
        <div className="mx-auto max-w-[760px] px-4">
          <p className="text-[var(--brand-charcoal)]/90 text-lg leading-relaxed">{guide.intro}</p>
        </div>
      </section>

      {/* Sections */}
      <section className="bg-white pb-10">
        <div className="mx-auto max-w-[760px] px-4 space-y-10">
          {guide.sections.map((s) => (
            <div key={s.heading}>
              <h2
                className="text-[var(--brand-deep-blue)] text-2xl md:text-3xl uppercase leading-tight mb-4"
                style={{ fontFamily: "var(--font-display)", fontWeight: 700 }}
              >
                {s.heading}
              </h2>
              <div className="space-y-4 text-[var(--brand-charcoal)]/85 text-base leading-relaxed">
                {s.paragraphs.map((p, i) => (
                  <p key={i}>{p}</p>
                ))}
              </div>
              {s.bullets && (
                <ul className="mt-4 space-y-2">
                  {s.bullets.map((b) => (
                    <li key={b} className="flex items-start gap-2 text-[var(--brand-charcoal)]/85 text-sm md:text-base">
                      <Check size={18} strokeWidth={3} className="text-[var(--brand-red)] shrink-0 mt-0.5" />
                      <span>{b}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          ))}
        </div>
      </section>

      {/* Mid CTA */}
      <section className="bg-[var(--brand-deep-blue)] text-white py-12">
        <div className="mx-auto max-w-[1200px] px-4 flex flex-col md:flex-row items-center justify-between gap-6 text-center md:text-left">
          <div>
            <h3
              className="text-2xl md:text-3xl uppercase leading-tight"
              style={{ fontFamily: "var(--font-display)", fontWeight: 700 }}
            >
              Ready for a Free Quote?
            </h3>
            <p className="mt-2 text-white/80">Fast callback. No pressure, no obligation.</p>
          </div>
          <div className="flex flex-col sm:flex-row gap-3">
            <button
              onClick={() => openQuoteModal()}
              className="inline-flex items-center justify-center gap-2 bg-[var(--brand-red)] hover:bg-[#a00d25] text-white font-bold uppercase tracking-wide px-6 py-3 rounded-md"
              style={{ fontFamily: "var(--font-display)" }}
            >
              Get Free Quote <ArrowRight size={18} />
            </button>
            <a
              href="tel:+15189001913"
              className="inline-flex items-center justify-center gap-2 bg-white/10 hover:bg-white/20 text-white font-bold uppercase tracking-wide px-6 py-3 rounded-md border border-white/30"
              style={{ fontFamily: "var(--font-display)" }}
            >
              <Phone size={18} /> (518) 900-1913
            </a>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="bg-white py-16 md:py-20">
        <div className="mx-auto max-w-[760px] px-4">
          <div className="text-center mb-10">
            <p
              className="text-[var(--brand-red)] font-semibold uppercase tracking-[0.2em] text-sm mb-3"
              style={{ fontFamily: "var(--font-display)" }}
            >
              FAQ
            </p>
            <h2
              className="text-[var(--brand-deep-blue)] text-3xl md:text-4xl uppercase leading-tight"
              style={{ fontFamily: "var(--font-display)", fontWeight: 700 }}
            >
              Frequently Asked Questions
            </h2>
          </div>
          <div className="space-y-3">
            {guide.faqs.map((f) => (
              <details
                key={f.q}
                className="group bg-[var(--brand-light-gray)] rounded-lg border border-gray-200 open:shadow-sm"
              >
                <summary className="cursor-pointer list-none px-5 py-4 font-semibold text-[var(--brand-deep-blue)] flex items-center justify-between gap-3">
                  {f.q}
                  <ChevronRight size={18} className="shrink-0 transition-transform group-open:rotate-90" />
                </summary>
                <p className="px-5 pb-4 text-[var(--brand-charcoal)]/85 text-sm leading-relaxed">{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* Related links */}
      <section className="bg-[var(--brand-light-gray)] py-14 md:py-16">
        <div className="mx-auto max-w-[760px] px-4">
          <h2
            className="text-[var(--brand-deep-blue)] text-2xl md:text-3xl uppercase leading-tight mb-6 text-center"
            style={{ fontFamily: "var(--font-display)", fontWeight: 700 }}
          >
            Related Pages
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {guide.relatedLinks.map((l) => (
              <Link
                key={l.to}
                to={l.to}
                className="flex items-center justify-between rounded-lg border border-gray-200 bg-white p-4 hover:border-[var(--brand-deep-blue)] transition-colors group"
              >
                <span
                  className="text-[var(--brand-deep-blue)] text-sm md:text-base font-bold uppercase leading-tight"
                  style={{ fontFamily: "var(--font-display)" }}
                >
                  {l.label}
                </span>
                <ChevronRight size={20} className="text-[var(--brand-charcoal)]/40 group-hover:text-[var(--brand-red)] transition-colors shrink-0" />
              </Link>
            ))}
          </div>
        </div>
      </section>
      <GuideCityLinks guideSlug={guide.slug} guideTitle={guide.title} />
      <InlineQuoteSection source="Guide" />
    </main>
  );
}

function GuideNotFound() {
  return (
    <main className="mx-auto max-w-xl px-4 py-24 text-center">
      <h1
        className="text-3xl md:text-4xl uppercase text-[var(--brand-deep-blue)]"
        style={{ fontFamily: "var(--font-display)", fontWeight: 700 }}
      >
        Guide not found
      </h1>
      <p className="mt-3 text-[var(--brand-charcoal)]/80">The guide you're looking for doesn't exist.</p>
      <Link
        to="/guides"
        className="inline-block mt-6 px-5 py-3 bg-[var(--brand-red)] text-white font-bold uppercase rounded-md"
      >
        View All Guides
      </Link>
    </main>
  );
}
